import { useState, useEffect, useCallback, useMemo } from "react";
import { GoldenSourceHelper } from "../../../app/Switch_Core_React_Standards";
import { fetchCompanyTaxonomy, setCompanyTaxonomy } from "../companyTaxonomyInteractionService";
import { fetchTaxonomyHeaders, fetchTaxonomyDetailsByHeader } from "../../site-admin/components/Workflow-esg/services/EsgTaxonomySyncService";
import { fetchCompanyScope, addScopeConcept, removeScopeConcept } from "./companyScopeInteractionService";

/**
 * 🎯 COMPANY ESG SCOPE CONTROLLER
 * Bridges "a taxonomy has been ingested" to "this company can make a
 * declaration": step 1 connects the company to an already-synced taxonomy
 * version (no re-ingestion), step 2 lets the company pick which concepts
 * out of that version's catalogue actually apply to them. That picked set
 * is the company's own ESG Scope — the thing a declaration will later be
 * entered against.
 */
export default function CompanyEsgScopeController(companyId, siteKey) {
  const [binding, setBinding] = useState(null);
  const [headers, setHeaders] = useState([]);
  const [selectedHeaderId, setSelectedHeaderId] = useState("");
  const [connecting, setConnecting] = useState(false);

  const [concepts, setConcepts] = useState([]);
  const [scope, setScope] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [busyTag, setBusyTag] = useState(null);

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const load = useCallback(async () => {
    if (!companyId) return;
    setLoading(true);
    setError("");
    try {
      const currentBinding = await fetchCompanyTaxonomy(companyId, { siteKey });
      setBinding(currentBinding);
      const boundHeaderId = GoldenSourceHelper.get(currentBinding, "HeaderId");

      if (boundHeaderId) {
        const [conceptRows, scopeRows] = await Promise.all([
          fetchTaxonomyDetailsByHeader(boundHeaderId, { siteKey }),
          fetchCompanyScope(companyId, { siteKey }),
        ]);
        setConcepts(conceptRows || []);
        setScope(scopeRows || []);
      } else {
        const allHeaders = await fetchTaxonomyHeaders({ siteKey });
        setHeaders(allHeaders || []);
        setConcepts([]);
        setScope([]);
      }
    } finally {
      setLoading(false);
    }
  }, [companyId, siteKey]);

  useEffect(() => {
    load();
  }, [load]);

  const handleConnect = async () => {
    if (!selectedHeaderId) return;
    setConnecting(true);
    setError("");
    try {
      await setCompanyTaxonomy(companyId, { headerId: selectedHeaderId, isBespoke: false }, { siteKey });
      await load();
    } catch (err) {
      setError(err.message || "Could not connect this taxonomy.");
    } finally {
      setConnecting(false);
    }
  };

  const scopedRowIds = useMemo(() => new Set(scope.map((s) => GoldenSourceHelper.get(s, "TaxonomyDetailRowId"))), [scope]);

  const filteredConcepts = useMemo(() => {
    const term = searchTerm.trim().toLowerCase();
    if (!term) return concepts;
    return concepts.filter((c) => {
      const tag = (GoldenSourceHelper.get(c, "XbrlTag") || "").toLowerCase();
      const label = (GoldenSourceHelper.get(c, "MeasureName") || "").toLowerCase();
      return tag.includes(term) || label.includes(term);
    });
  }, [concepts, searchTerm]);

  const handleAddToScope = async (concept) => {
    const rowId = GoldenSourceHelper.get(concept, "RowId");
    setBusyTag(rowId);
    setError("");
    try {
      await addScopeConcept(companyId, rowId, { siteKey });
      const scopeRows = await fetchCompanyScope(companyId, { siteKey });
      setScope(scopeRows || []);
    } catch (err) {
      setError(err.message || "Could not add this concept to scope.");
    } finally {
      setBusyTag(null);
    }
  };

  const handleRemoveFromScope = async (taxonomyDetailRowId) => {
    setBusyTag(taxonomyDetailRowId);
    setError("");
    try {
      await removeScopeConcept(companyId, taxonomyDetailRowId, { siteKey });
      const scopeRows = await fetchCompanyScope(companyId, { siteKey });
      setScope(scopeRows || []);
    } catch (err) {
      setError(err.message || "Could not remove this concept from scope.");
    } finally {
      setBusyTag(null);
    }
  };

  const boundHeaderId = GoldenSourceHelper.get(binding, "HeaderId");

  return {
    loading,
    error,
    binding,
    boundHeaderId,
    isConnected: !!boundHeaderId,
    headers,
    selectedHeaderId,
    setSelectedHeaderId,
    connecting,
    handleConnect,
    searchTerm,
    setSearchTerm,
    filteredConcepts,
    scope,
    scopedRowIds,
    busyTag,
    handleAddToScope,
    handleRemoveFromScope,
  };
}
